/**
 * Descaro · 3D Glowing Card Carousel Slider
 * Mac-style fluid physics, lerp mouse damping, infinite circular loop & URL deep-linking.
 */

(function () {
  class DescaroRitualesSlider {
    constructor(container) {
      this.container = container;
      this.deck = container.querySelector('[data-ritual-deck]');
      this.cards = Array.from(container.querySelectorAll('[data-ritual-card]'));
      this.dots = Array.from(container.querySelectorAll('[data-ritual-dot]'));
      this.total = this.cards.length;

      if (this.total === 0) return;

      // Text elements to synchronize
      this.titleEl = container.querySelector('[data-ritual-title]');
      this.bodyEl = container.querySelector('[data-ritual-body]');
      this.ctaEl = container.querySelector('[data-ritual-cta]');

      // State
      this.currentIndex = 0;
      this.isAnimating = false;

      // Mac-style fluid tilt physics (Lerp)
      this.targetRotX = 0;
      this.targetRotY = 0;
      this.currentRotX = 0;
      this.currentRotY = 0;
      this.isHoveringActive = false;
      this.rafId = null;

      // Check initial index from data attribute if present (e.g. metaobject single page)
      const attrInit = parseInt(container.getAttribute('data-initial-index'), 10);
      if (!isNaN(attrInit) && attrInit >= 0 && attrInit < this.total) {
        this.currentIndex = attrInit;
      }

      // Check URL hash for initial card deep-linking (hash takes priority)
      this.initFromHash();

      // Setup listeners
      this.bindEvents();

      // Initial state render
      this.updateState(this.currentIndex, false);
    }

    initFromHash() {
      const hash = (window.location.hash || '').replace('#', '').trim().toLowerCase();
      if (!hash) return;

      const matchedIndex = this.cards.findIndex((card) => {
        const slug = (card.getAttribute('data-slug') || '').toLowerCase();
        const id = (card.getAttribute('id') || '').toLowerCase();
        return slug === hash || id === hash;
      });

      if (matchedIndex !== -1) {
        this.currentIndex = matchedIndex;
      }
    }

    bindEvents() {
      // Click on cards
      this.cards.forEach((card, idx) => {
        card.addEventListener('click', (e) => {
          if (idx !== this.currentIndex) {
            e.preventDefault();
            this.goTo(idx);
          }
        });
      });

      // Click on dots
      this.dots.forEach((dot, idx) => {
        dot.addEventListener('click', () => {
          this.goTo(idx);
        });
      });

      // Keyboard navigation
      this.container.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.prev();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.next();
        }
      });

      // Swipe / touch navigation with gesture smoothing
      let touchStartX = 0;
      let touchStartY = 0;

      this.deck.addEventListener(
        'touchstart',
        (e) => {
          touchStartX = e.touches[0].clientX;
          touchStartY = e.touches[0].clientY;
        },
        { passive: true }
      );

      this.deck.addEventListener(
        'touchend',
        (e) => {
          const deltaX = e.changedTouches[0].clientX - touchStartX;
          const deltaY = e.changedTouches[0].clientY - touchStartY;

          // Horizontal swipe threshold 35px
          if (Math.abs(deltaX) > 35 && Math.abs(deltaX) > Math.abs(deltaY)) {
            if (deltaX < 0) {
              this.next();
            } else {
              this.prev();
            }
          }
        },
        { passive: true }
      );

      // Mac-grade fluid Lerp loop for mouse movement
      const updatePhysics = () => {
        if (this.isHoveringActive) {
          this.currentRotX += (this.targetRotX - this.currentRotX) * 0.08;
          this.currentRotY += (this.targetRotY - this.currentRotY) * 0.08;
        } else {
          // Gently return to rest
          this.currentRotX += (0 - this.currentRotX) * 0.08;
          this.currentRotY += (0 - this.currentRotY) * 0.08;
        }

        const activeCard = this.cards[this.currentIndex];
        if (activeCard && (Math.abs(this.currentRotX) > 0.01 || Math.abs(this.currentRotY) > 0.01 || this.isHoveringActive)) {
          activeCard.style.transform = `translate3d(0, 0, 70px) scale(1.06) rotateX(${this.currentRotX.toFixed(3)}deg) rotateY(${this.currentRotY.toFixed(3)}deg)`;
        } else if (activeCard && !this.isHoveringActive) {
          activeCard.style.transform = '';
        }

        this.rafId = requestAnimationFrame(updatePhysics);
      };

      this.rafId = requestAnimationFrame(updatePhysics);

      this.deck.addEventListener('mousemove', (e) => {
        const activeCard = this.cards[this.currentIndex];
        if (!activeCard) return;

        const rect = activeCard.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        if (
          mouseX >= 0 &&
          mouseX <= rect.width &&
          mouseY >= 0 &&
          mouseY <= rect.height
        ) {
          this.isHoveringActive = true;
          const xPercent = (mouseX / rect.width) * 100;
          const yPercent = (mouseY / rect.height) * 100;

          activeCard.style.setProperty('--mouse-x', `${xPercent.toFixed(1)}%`);
          activeCard.style.setProperty('--mouse-y', `${yPercent.toFixed(1)}%`);

          // Target tilt (max 9 degrees for subtle elegance)
          this.targetRotY = (mouseX / rect.width - 0.5) * 9;
          this.targetRotX = (0.5 - mouseY / rect.height) * 9;
        } else {
          this.isHoveringActive = false;
          this.targetRotX = 0;
          this.targetRotY = 0;
        }
      });

      this.deck.addEventListener('mouseleave', () => {
        this.isHoveringActive = false;
        this.targetRotX = 0;
        this.targetRotY = 0;
      });

      // Browser back/forward navigation
      window.addEventListener('popstate', () => {
        this.initFromHash();
        this.updateState(this.currentIndex, true);
      });
    }

    goTo(targetIndex) {
      if (this.isAnimating) return;
      const normalized = ((targetIndex % this.total) + this.total) % this.total;
      if (normalized === this.currentIndex) return;

      this.isAnimating = true;
      this.isHoveringActive = false;
      this.targetRotX = 0;
      this.targetRotY = 0;
      this.currentIndex = normalized;
      this.updateState(this.currentIndex, true);

      setTimeout(() => {
        this.isAnimating = false;
      }, 650);
    }

    next() {
      this.goTo(this.currentIndex + 1);
    }

    prev() {
      this.goTo(this.currentIndex - 1);
    }

    updateState(activeIndex, updateHistory = true) {
      const N = this.total;

      // 1. Update card 3D classes
      this.cards.forEach((card, idx) => {
        card.classList.remove(
          'dsc-rituales__card--active',
          'dsc-rituales__card--prev',
          'dsc-rituales__card--next',
          'dsc-rituales__card--hidden-left',
          'dsc-rituales__card--hidden-right'
        );
        card.style.transform = '';

        if (N === 1) {
          card.classList.add('dsc-rituales__card--active');
          card.setAttribute('aria-hidden', 'false');
          return;
        }

        // Circular modulo offset
        let diff = (idx - activeIndex) % N;
        if (diff > N / 2) diff -= N;
        if (diff < -N / 2) diff += N;

        if (diff === 0) {
          card.classList.add('dsc-rituales__card--active');
          card.setAttribute('aria-hidden', 'false');
          card.setAttribute('tabindex', '0');
        } else if (diff === -1 || (N === 2 && diff === 1 && activeIndex === 1)) {
          card.classList.add('dsc-rituales__card--prev');
          card.setAttribute('aria-hidden', 'true');
          card.setAttribute('tabindex', '-1');
        } else if (diff === 1) {
          card.classList.add('dsc-rituales__card--next');
          card.setAttribute('aria-hidden', 'true');
          card.setAttribute('tabindex', '-1');
        } else if (diff < 0) {
          card.classList.add('dsc-rituales__card--hidden-left');
          card.setAttribute('aria-hidden', 'true');
          card.setAttribute('tabindex', '-1');
        } else {
          card.classList.add('dsc-rituales__card--hidden-right');
          card.setAttribute('aria-hidden', 'true');
          card.setAttribute('tabindex', '-1');
        }
      });

      // 2. Update pagination dots
      this.dots.forEach((dot, idx) => {
        const isActive = idx === activeIndex;
        dot.classList.toggle('dsc-rituales__dot--active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
        dot.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      // 3. Update right column copy
      this.updateCopy(activeIndex);

      // 4. Update URL hash for SEO & shareability
      const activeCard = this.cards[activeIndex];
      const slug = activeCard ? activeCard.getAttribute('data-slug') : '';
      if (updateHistory && slug) {
        history.replaceState(null, '', `#${slug}`);
      }
    }

    updateCopy(activeIndex) {
      const activeCard = this.cards[activeIndex];
      if (!activeCard) return;

      const heading = activeCard.getAttribute('data-heading') || '';
      const bodyHtml = activeCard.getAttribute('data-body') || '';
      const ctaLabel = activeCard.getAttribute('data-cta-label') || '';
      const ctaUrl = activeCard.getAttribute('data-cta-url') || '';

      const animTargets = [this.titleEl, this.bodyEl, this.ctaEl].filter(Boolean);

      // Smooth fade swap
      animTargets.forEach((el) => {
        el.classList.add('dsc-rituales__text--fade-out');
        el.classList.remove('dsc-rituales__text--fade-in');
      });

      setTimeout(() => {
        if (this.titleEl && heading) {
          this.titleEl.textContent = heading;
        }

        if (this.bodyEl && bodyHtml) {
          let cleanBody = bodyHtml;
          if (cleanBody.includes('para transfo') && !cleanBody.includes('transformar')) {
            cleanBody = cleanBody.replace('para transfo', 'para transformar una pausa. Empieza por una receta y guarda espacio para lo inesperado.');
          }
          this.bodyEl.innerHTML = cleanBody;
        }

        if (this.ctaEl) {
          if (ctaLabel && ctaUrl) {
            const textSpan = this.ctaEl.querySelector('[data-ritual-cta-text]');
            if (textSpan) {
              textSpan.textContent = ctaLabel;
            } else {
              this.ctaEl.innerHTML = `<span data-ritual-cta-text>${ctaLabel}</span> <span class="dsc-rituales__cta-arrow" aria-hidden="true">&rarr;</span>`;
            }
            if (!this.ctaEl.querySelector('.dsc-rituales__cta-arrow')) {
              const arrow = document.createElement('span');
              arrow.className = 'dsc-rituales__cta-arrow';
              arrow.setAttribute('aria-hidden', 'true');
              arrow.innerHTML = '&rarr;';
              this.ctaEl.appendChild(arrow);
            }
            this.ctaEl.href = ctaUrl;
            this.ctaEl.style.display = 'inline-flex';
          } else {
            this.ctaEl.style.display = 'none';
          }
        }

        animTargets.forEach((el) => {
          el.classList.remove('dsc-rituales__text--fade-out');
          el.classList.add('dsc-rituales__text--fade-in');
        });
      }, 160);
    }
  }

  // Initialize all instances
  function initRitualesSliders() {
    const sliders = document.querySelectorAll('[data-rituales-slider]');
    sliders.forEach((slider) => {
      if (!slider._ritualesSlider) {
        slider._ritualesSlider = new DescaroRitualesSlider(slider);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRitualesSliders);
  } else {
    initRitualesSliders();
  }

  // Support Shopify Theme Customizer events
  document.addEventListener('shopify:section:load', initRitualesSliders);
  document.addEventListener('shopify:section:select', initRitualesSliders);
})();
